import gsap from 'gsap';

// Add loading animation CSS
const loadingStyles = document.createElement('style');
loadingStyles.textContent = `
    .product-categories__products-grid.loading-pulse {
        position: relative;
    }

    .product-categories__products-grid.loading-pulse::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 50px;
        height: 50px;
        border: 3px solid rgba(0, 57, 77, 0.1);
        border-top-color: rgba(0, 57, 77, 0.6);
        border-radius: 50%;
        animation: spin 1s linear infinite;
        z-index: 10;
    }

    @keyframes spin {
        to { transform: translate(-50%, -50%) rotate(360deg); }
    }
`;
document.head.appendChild(loadingStyles);

// Track which accordion triggers already have event listeners attached
const processedAccordionTriggers = new Set();

function setupFilterButton() {
    const filterThirdLevelButtons = document.querySelectorAll('.filter-third-level-button');
    const filterThirdLevelModal = document.querySelector('.filter-third-level-modal');
    const modalCloseButton = document.querySelector('.modal-close');
    const applyButton = filterThirdLevelModal?.querySelector('.modal-apply');
    const clearButton = filterThirdLevelModal?.querySelector('.modal-clear');

    if (!filterThirdLevelButtons.length || !filterThirdLevelModal) {
        console.warn('Filter elements not found, waiting for DOM...');
        return;
    }

    filterThirdLevelButtons?.forEach(button => {
        button.addEventListener('click', function () {
            filterThirdLevelModal.classList.add('active');
            // Disable page scroll
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';

            gsap.to(filterThirdLevelModal, {
                backgroundColor: 'rgba(0, 57, 77, 0.15)',
                duration: 0.5,
                ease: 'power1.in'
            });

            gsap.to(filterThirdLevelModal.querySelector('.modal-container'), {
                x: 0,
                duration: 0.5,
                ease: 'power1.in'
            });
        });
    });

    function closeModal() {
        gsap.to(filterThirdLevelModal.querySelector('.modal-container'), {
            x: '-200%',
            duration: 0.5,
            ease: 'power1.in'
        });

        gsap.to(filterThirdLevelModal, {
            backgroundColor: 'rgba(0, 57, 77, 0)',
            duration: 0.5,
            ease: 'power1.in',
            onComplete: () => {
                filterThirdLevelModal.classList.remove('active');
                // Re-enable page scroll
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.width = '';
            }
        });
    }

    // Update close button handler
    modalCloseButton.addEventListener('click', closeModal);

    // Update outside click handler
    filterThirdLevelModal.addEventListener('click', function(e) {
        const modalContainer = filterThirdLevelModal.querySelector('.modal-container');
        if (!modalContainer.contains(e.target) && e.target !== modalContainer) {
            e.stopPropagation();
            e.preventDefault();
            closeModal();
        }
    });

    // Update escape key handler
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && filterThirdLevelModal.classList.contains('active')) {
            closeModal();
        }
    });

    // Add handler for apply button
    applyButton?.addEventListener('click', () => {
        // Close modal after form submission
        setTimeout(closeModal, 100);
    });

    // Add handler for clear button
    clearButton?.addEventListener('click', () => {
        // Find all checkboxes in the modal and uncheck them
        const checkboxes = filterThirdLevelModal.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach(checkbox => {
            checkbox.checked = false;
        });
    });
}

// Setup accordion functionality separately to avoid duplication
function setupAccordions() {
    const accordionTriggers = document.querySelectorAll('.accordion-trigger');

    accordionTriggers.forEach(trigger => {
        // Skip triggers that already have event listeners
        if (processedAccordionTriggers.has(trigger)) {
            return;
        }

        // Mark this trigger as processed
        processedAccordionTriggers.add(trigger);

        trigger.addEventListener('click', function(e) {
            // Prevent event bubbling
            e.stopPropagation();

            const accordionId = this.dataset.accordion;
            const content = document.getElementById(`accordion-${accordionId}`);

            if (!content) {
                console.warn(`Accordion content with ID accordion-${accordionId} not found`);
                return;
            }

            const verticalLine = this.querySelector('.vertical-line');

            // Toggle active class for styling
            content.classList.toggle('active');
            verticalLine?.classList.toggle('hidden');

            // Use GSAP to animate height
            if (content.classList.contains('active')) {
                gsap.to(content, {
                    height: content.scrollHeight,
                    duration: 0.3,
                    ease: "power2.inOut"
                });
            } else {
                gsap.to(content, {
                    height: 0,
                    duration: 0.3,
                    ease: "power2.inOut"
                });
            }
        });
    });
}

// Call setupFilterButton on DOMContentLoaded as well
document.addEventListener('DOMContentLoaded', () => {
    setupFilterButton();
    setupAccordions();
});

// Update the observer to be more specific
const observer = new MutationObserver((mutations) => {
    let shouldSetupFilter = false;
    let shouldSetupAccordions = false;

    mutations.forEach((mutation) => {
        const hasFilterElements = mutation.target.querySelector('.filter-third-level-button, .filter-third-level-modal');
        const hasAccordionElements = mutation.target.querySelector('.accordion-trigger');

        if (hasFilterElements) {
            shouldSetupFilter = true;
        }

        if (hasAccordionElements) {
            shouldSetupAccordions = true;
        }
    });

    if (shouldSetupFilter) {
        setupFilterButton();
    }

    if (shouldSetupAccordions) {
        setupAccordions();
    }
});

// Start observing with modified parameters
observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true // Add this to catch class changes
});

function initAutocomplete() {
    const addressInput = document.getElementById('fui-businessPersonalAccountRegistration-siagop-fields-billingAddress-address1');
    const shippingInput = document.getElementById('fui-businessPersonalAccountRegistration-siagop-fields-shippingAddress-autoComplete');

    if (addressInput) {
        const autocomplete = new google.maps.places.Autocomplete(addressInput, { types: ['geocode'] });
        autocomplete.addListener('place_changed', function () {
            const place = autocomplete.getPlace();
            console.log("Billing Address Details:", place);
        });
    }

    if (shippingInput) {
        const autocompleteShipping = new google.maps.places.Autocomplete(shippingInput, { types: ['geocode'] });
        autocompleteShipping.addListener('place_changed', function () {
            const place = autocompleteShipping.getPlace();
            console.log("Shipping Address Details:", place);
        });
    }
}

initAutocomplete();

// Ensure product card links work as regular links and bypass htmx/Sprig
function setupProductCardLinks() {
    const productCards = document.querySelectorAll('.product-card .hover-link');
    productCards.forEach(link => {
        // Prevent htmx from processing these links
        link.setAttribute('hx-disable', '');

        // Stop event propagation to prevent any parent handlers from interfering
        link.addEventListener('click', function(e) {
            e.stopPropagation();
            // Let the browser handle the navigation normally
        }, { capture: true });
    });
}

// Set up product card links on page load
document.addEventListener('DOMContentLoaded', setupProductCardLinks);

// Also set up product card links when new content is loaded via AJAX
const productObserver = new MutationObserver((mutations) => {
    let hasNewProductCards = false;

    mutations.forEach((mutation) => {
        const hasProductCards = mutation.target.querySelector && mutation.target.querySelector('.product-card');
        if (hasProductCards) {
            hasNewProductCards = true;
        }
    });

    if (hasNewProductCards) {
        setupProductCardLinks();
    }
});

productObserver.observe(document.body, {
    childList: true,
    subtree: true
});

// Debug: Log filter elements on page
console.log('[Debug] Page loaded. Looking for filter elements...');
console.log('[Debug] Level 2 filters found:', document.querySelectorAll('.filter-level2').length);
console.log('[Debug] Level 2 filter details:', Array.from(document.querySelectorAll('.filter-level2')).map(el => ({
    text: el.textContent.trim(),
    classes: el.className,
    dataset: el.dataset
})));
console.log('[Debug] Modal form:', document.getElementById('filter-modal-form'));

// URL Management for Pretty URLs and Filters
const urlManager = {
    // Get current sort parameter
    getCurrentSort() {
        const params = new URLSearchParams(window.location.search);
        const sort = params.get('sort') || 'featured';
        console.log('[urlManager] Current sort:', sort);
        return sort;
    },

    // Build pretty URL for single selection
    buildPrettyUrl(mainSlug, level2Slug, level3Slug) {
        console.log('[urlManager] Building pretty URL:', { mainSlug, level2Slug, level3Slug });
        let url = `/${mainSlug}`;
        if (level2Slug) {
            url += `/${level2Slug}`;
            if (level3Slug) {
                url += `/${level3Slug}`;
            }
        }
        
        // Add sort parameter if not default
        const sort = this.getCurrentSort();
        if (sort && sort !== 'featured') {
            url += `?sort=${sort}`;
        }
        
        console.log('[urlManager] Built URL:', url);
        return url;
    },

    // Build parameter URL for multiple selections
    buildParamUrl(mainSlug, categoryIds) {
        console.log('[urlManager] Building param URL:', { mainSlug, categoryIds });
        const params = new URLSearchParams();
        
        // Add category IDs
        categoryIds.forEach(id => {
            params.append('productCategory[]', id);
        });
        
        // Add sort if not default
        const sort = this.getCurrentSort();
        if (sort && sort !== 'featured') {
            params.set('sort', sort);
        }
        
        const url = `/${mainSlug}?${params.toString()}`;
        console.log('[urlManager] Built param URL:', url);
        return url;
    },

    // Update browser URL without reload
    updateUrl(url) {
        console.log('[urlManager] Updating browser URL to:', url);
        window.history.pushState({}, '', url);
    },

    // Start loading animation
    startLoading() {
        const productsGrid = document.querySelector('.product-categories__products-grid');
        const categoriesContainer = document.querySelector('.product-categories');

        if (productsGrid) {
            productsGrid.classList.add('loading-pulse');
            // Add opacity and pointer events
            productsGrid.style.opacity = '0.5';
            productsGrid.style.pointerEvents = 'none';
            productsGrid.style.transition = 'opacity 0.3s ease';
        }

        if (categoriesContainer) {
            categoriesContainer.classList.add('loading');
        }
    },

    // Stop loading animation
    stopLoading() {
        const productsGrid = document.querySelector('.product-categories__products-grid');
        const categoriesContainer = document.querySelector('.product-categories');

        if (productsGrid) {
            productsGrid.classList.remove('loading-pulse');
            // Remove opacity and pointer events
            productsGrid.style.opacity = '';
            productsGrid.style.pointerEvents = '';
        }

        if (categoriesContainer) {
            categoriesContainer.classList.remove('loading');
        }
    },

    // Trigger Sprig refresh with specified URL
    refreshContent(url = null) {
        // Use provided URL or fall back to current location
        const targetUrl = url || window.location.href;
        console.log('[urlManager] Triggering Sprig refresh for URL:', targetUrl);

        // Start loading animation
        this.startLoading();

        // Find the entire product-categories container (includes filters + products)
        const categoriesContainer = document.querySelector('.product-categories') ||
                                    document.querySelector('[sprig]');

        console.log('[urlManager] Categories container found:', !!categoriesContainer);
        console.log('[urlManager] htmx available:', typeof htmx !== 'undefined');

        if (categoriesContainer && typeof htmx !== 'undefined') {
            console.log('[urlManager] Making AJAX request with htmx to:', targetUrl);

            // Use htmx.ajax to make a request to the target URL and update entire section
            // This includes both filters AND products so Level 3 tags display properly
            htmx.ajax('GET', targetUrl, {
                target: categoriesContainer,
                swap: 'outerHTML',
                select: '.product-categories',
                show: false  // Prevent htmx from auto-scrolling
            });

            // Loading animation will stop when content is replaced
        } else {
            console.error('[urlManager] Cannot refresh - missing container or htmx');
            this.stopLoading();
        }
    }
};

// Set initial active states based on URL on page load
function setInitialActiveStates() {
    console.log('[Filter] Setting initial active states based on URL');

    // Parse current URL path
    const pathParts = window.location.pathname.split('/').filter(part => part !== '');
    console.log('[Filter] URL path parts:', pathParts);

    // If we have at least 2 parts (e.g., /personal-protection/gloves)
    // The second part is the level 2 slug
    if (pathParts.length >= 2) {
        const level2Slug = pathParts[1];
        console.log('[Filter] Looking for level2 slug:', level2Slug);

        // Find the button with matching slug
        const buttons = document.querySelectorAll('.filter-level2');
        buttons.forEach(button => {
            if (button.dataset.slug === level2Slug) {
                console.log('[Filter] Found matching button, setting active:', button);
                button.classList.add('active');
            } else {
                button.classList.remove('active');
            }
        });
    } else {
        // No level 2 selected, remove all active states
        console.log('[Filter] No level2 in URL, clearing all active states');
        document.querySelectorAll('.filter-level2').forEach(btn => {
            btn.classList.remove('active');
        });
    }
}

// Call on DOMContentLoaded
document.addEventListener('DOMContentLoaded', setInitialActiveStates);

// Also call after content is loaded via Sprig/htmx
// Store scroll position before swap
let savedScrollPosition = 0;

document.body.addEventListener('htmx:beforeSwap', function(event) {
    if (event.detail.target.classList.contains('product-categories') ||
        event.detail.target.closest('.product-categories')) {
        // Save current scroll position
        savedScrollPosition = window.pageYOffset;
        console.log('[Filter] Saved scroll position:', savedScrollPosition);
    }
});

document.body.addEventListener('htmx:afterSwap', function(event) {
    // Set active states if the product-categories container was swapped
    if (event.detail.target.classList.contains('product-categories') ||
        event.detail.target.closest('.product-categories')) {
        console.log('[Filter] Content swapped, updating active states');
        setInitialActiveStates();

        // Restore scroll position to prevent jumping
        window.scrollTo(0, savedScrollPosition);
        console.log('[Filter] Restored scroll position:', savedScrollPosition);

        // Ensure loading state is cleared
        urlManager.stopLoading();
    }
});

// Clear loading state on htmx errors
document.body.addEventListener('htmx:responseError', function(event) {
    console.error('[Filter] htmx error:', event.detail);
    urlManager.stopLoading();
});

document.body.addEventListener('htmx:sendError', function(event) {
    console.error('[Filter] htmx send error:', event.detail);
    urlManager.stopLoading();
});

// Handle clearing all Level 3 filters
document.addEventListener('click', function(e) {
    const clearButton = e.target.closest('.clear-all-level3-filters');
    if (!clearButton) return;

    console.log('[Filter] Clear all Level 3 filters clicked');
    e.preventDefault();
    e.stopPropagation();

    const mainSlug = clearButton.dataset.mainSlug;
    const url = `/${mainSlug}`;

    console.log('[Filter] Clearing all filters, navigating to:', url);

    // Update URL and refresh with the new URL
    urlManager.updateUrl(url);
    urlManager.refreshContent(url);
});

// Handle removing individual Level 3 filters
document.addEventListener('click', function(e) {
    const removeButton = e.target.closest('.remove-level3-filter');
    if (!removeButton) return;

    // Immediately stop propagation to prevent any parent handlers
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    console.log('[Filter] Remove Level 3 filter clicked');

    const categoryId = removeButton.dataset.categoryId;
    const mainSlug = removeButton.dataset.mainSlug;

    console.log('[Filter] Removing category ID:', categoryId, 'type:', typeof categoryId);

    // Get current URL parameters
    const params = new URLSearchParams(window.location.search);
    const currentCategories = params.getAll('productCategory[]');

    console.log('[Filter] Current categories:', currentCategories, 'types:', currentCategories.map(id => typeof id));

    // Remove the specific category (ensure type-safe comparison by converting both to strings)
    const updatedCategories = currentCategories.filter(id => String(id) !== String(categoryId));

    console.log('[Filter] Updated categories:', updatedCategories);

    // Build new URL
    let url;
    if (updatedCategories.length === 0) {
        // No more Level 3 filters, go to main category
        url = `/${mainSlug}`;
    } else if (updatedCategories.length === 1) {
        // Only one left - could use pretty URL if we have the slug
        // For now, use query param
        url = `/${mainSlug}?productCategory[]=${updatedCategories[0]}`;
    } else {
        // Multiple still selected
        const newParams = new URLSearchParams();
        updatedCategories.forEach(id => {
            newParams.append('productCategory[]', id);
        });

        // Preserve sort if present
        const sort = params.get('sort');
        if (sort && sort !== 'featured') {
            newParams.set('sort', sort);
        }

        url = `/${mainSlug}?${newParams.toString()}`;
    }

    console.log('[Filter] New URL:', url);

    // Update URL and refresh with the new URL
    urlManager.updateUrl(url);
    urlManager.refreshContent(url);
});

// Handle Level 2 filter clicks
document.addEventListener('click', function(e) {
    // Exclude product cards and their elements from any filter processing
    if (e.target.closest('.product-card') || e.target.closest('.hover-link')) {
        return;
    }

    // Only process clicks on filter elements, ignore all other clicks
    if (!e.target.matches('.filter-level2')) {
        return;
    }

    console.log('[Filter] Level 2 filter clicked');
    e.preventDefault();

    const button = e.target;
    const mainSlug = button.dataset.mainSlug;
    const level2Slug = button.dataset.slug;

    console.log('[Filter] Filter data:', {
        mainSlug,
        level2Slug,
        allDataAttributes: button.dataset
    });

    // Optimistic UI update - update active state immediately
    console.log('[Filter] Updating active states optimistically');

    // Remove active from all level 2 filters
    document.querySelectorAll('.filter-level2').forEach(btn => {
        btn.classList.remove('active');
    });

    // Add active to clicked button
    button.classList.add('active');

    // Build and update URL
    const url = urlManager.buildPrettyUrl(mainSlug, level2Slug);
    urlManager.updateUrl(url);

    // Trigger Sprig refresh with the new URL
    urlManager.refreshContent(url);
});

// Handle modal apply button (using event delegation to work after htmx swaps)
document.addEventListener('click', function(e) {
    const applyBtn = e.target.closest('.modal-apply');
    if (!applyBtn) return;

    console.log('[Modal] Apply button clicked');
    e.preventDefault();
    e.stopPropagation();

    const form = document.getElementById('filter-modal-form');
    if (!form) {
        console.error('[Modal] Form not found');
        return;
    }

    const mainSlug = form.dataset.mainSlug;
    const checkboxes = form.querySelectorAll('input[type="checkbox"]:checked');

    console.log('[Modal] Current URL:', window.location.href);
    console.log('[Modal] Current pathname:', window.location.pathname);
    console.log('[Modal] Form data:', {
        mainSlug,
        mainSlugType: typeof mainSlug,
        checkedCount: checkboxes.length,
        checkedValues: Array.from(checkboxes).map(cb => ({
            value: cb.value,
            slug: cb.dataset.slug,
            parentSlug: cb.dataset.parentSlug
        }))
    });

    let url;
    if (checkboxes.length === 0) {
        console.log('[Modal] No selection - navigating to main category');
        url = `/${mainSlug}`;
    } else if (checkboxes.length === 1) {
        console.log('[Modal] Single selection - using pretty URL');
        const checkbox = checkboxes[0];
        const level3Slug = checkbox.dataset.slug;
        const level2Slug = checkbox.dataset.parentSlug;

        url = urlManager.buildPrettyUrl(mainSlug, level2Slug, level3Slug);
    } else {
        console.log('[Modal] Multiple selections - using parameters');
        const categoryIds = Array.from(checkboxes).map(cb => cb.value);
        // Build clean URL with ONLY mainSlug, removing any level2 path segments
        url = urlManager.buildParamUrl(mainSlug, categoryIds);
        console.log('[Modal] Built clean URL (removing any level2):', url);
    }

    // Update the URL
    urlManager.updateUrl(url);

    // Clear active state from level 2 filters since we're now using modal filters
    document.querySelectorAll('.filter-level2').forEach(btn => {
        btn.classList.remove('active');
    });

    // Close modal
    const modal = document.querySelector('.filter-third-level-modal');
    if (modal) {
        modal.classList.remove('active');
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
    }

    // Trigger Sprig refresh with the new URL
    console.log('[Modal] Triggering refresh with URL:', url);
    urlManager.refreshContent(url);
});

// Handle modal clear button (using event delegation)
document.addEventListener('click', function(e) {
    const clearBtn = e.target.closest('.modal-clear');
    if (!clearBtn) return;

    console.log('[Modal] Clear button clicked');
    e.preventDefault();
    e.stopPropagation();

    const form = document.getElementById('filter-modal-form');
    if (!form) {
        console.error('[Modal] Form not found');
        return;
    }

    // Uncheck all checkboxes
    const checkboxes = form.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(cb => cb.checked = false);

    // Navigate to main category
    const mainSlug = form.dataset.mainSlug;
    const url = `/${mainSlug}`;
    urlManager.updateUrl(url);

    // Close modal
    const modal = document.querySelector('.filter-third-level-modal');
    if (modal) {
        modal.classList.remove('active');
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
    }

    // Trigger Sprig refresh with the new URL
    urlManager.refreshContent(url);
});

// Intercept Load More button requests and add current URL parameters
document.body.addEventListener('htmx:configRequest', function(event) {
    // Check if this is the load more button
    if (event.detail.elt.id === 'load-more-oob' || event.detail.elt.classList.contains('load-more-btn')) {
        // Update the request path to use the current URL path
        // This ensures we're requesting the same URL with filters applied
        event.detail.path = window.location.pathname + window.location.search;

        // Get current URL parameters
        const params = new URLSearchParams(window.location.search);

        // Add all current parameters to the htmx request
        params.forEach((value, key) => {
            event.detail.parameters[key] = value;
        });

        // Also add offset if it's not already there
        if (!event.detail.parameters.offset) {
            const offset = event.detail.elt.dataset.offset;
            if (offset) {
                event.detail.parameters.offset = offset;
            }
        }
    }
});

// Handle Load More button loading state
document.body.addEventListener('htmx:beforeRequest', function(event) {
    const button = event.detail.elt;
    if (button.id === 'load-more-oob' || button.classList.contains('load-more-btn')) {
        // Store original text
        button.dataset.originalText = button.textContent;
        // Update button text and disable it
        button.textContent = 'Loading...';
        button.disabled = true;
        button.style.opacity = '0.6';
        button.style.cursor = 'not-allowed';
    }
});

// Reset Load More button after request completes
document.body.addEventListener('htmx:afterSwap', function(event) {
    const button = event.detail.elt;
    if (button.id === 'load-more-oob' || button.classList.contains('load-more-btn')) {
        // Restore original text and state
        if (button.dataset.originalText) {
            button.textContent = button.dataset.originalText;
        }
        button.disabled = false;
        button.style.opacity = '';
        button.style.cursor = '';
    }
});

// Also reset on error
document.body.addEventListener('htmx:responseError', function(event) {
    const button = event.detail.elt;
    if (button.id === 'load-more-oob' || button.classList.contains('load-more-btn')) {
        if (button.dataset.originalText) {
            button.textContent = button.dataset.originalText;
        }
        button.disabled = false;
        button.style.opacity = '';
        button.style.cursor = '';
    }
});
